import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import httpService from '../../services/http.service';
import { ShimmerChart } from '../Shared/Shimmer';
import {
    PERIODICITY_DAILY,
    PERIODICITY_MONTHLY,
    PERIODICITY_WEEKLY,
    PERIODICITY_YEARLY,
} from '../../utils/index';

const PERIODS = [
    [PERIODICITY_DAILY, "Aujourd'hui"],
    [PERIODICITY_WEEKLY, 'Cette semaine'],
    [PERIODICITY_MONTHLY, 'Ce mois'],
    [PERIODICITY_YEARLY, 'Cette année'],
];
const COLORS = ['#1880a1', '#f5a623', '#2e7d32', '#8e44ad', '#e74c3c'];
const COMMON = {
    chart: { toolbar: { show: false }, fontFamily: 'inherit', animations: { enabled: true, speed: 400 } },
    grid: { borderColor: '#eef1f5', strokeDashArray: 4 },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', fontSize: '12px', markers: { radius: 12 } },
    noData: { text: '' },
};

const ChartCard = ({ title, subtitle, loading, empty, emptyLabel, children }) => (
    <div className="card dashboard-chart-card">
        <div className="header">
            <h2>{title}</h2>
            {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        <div className="body">
            {loading ? (
                <ShimmerChart height={260} />
            ) : empty ? (
                <div className="text-center text-muted py-5">{emptyLabel}</div>
            ) : children}
        </div>
    </div>
);

/**
 * Graphiques du tableau de bord : nouveaux et actifs par période, gains des comptes monétisés, pays les plus actifs.
 * Les données de la période et des comptes monétisés viennent du tableau de bord ; les pays sont chargés ici.
 */
const DashboardCharts = ({ newUsers, activeUsers, loadingUsers, monetizedAccounts, loadingMonetized, multiplier = 1 }) => {
    const { t } = useTranslation();
    const [countries, setCountries] = useState(null);

    useEffect(() => {
        let cancelled = false;
        httpService.get('/stats/top-active-users-by-country')
            .then(({ data }) => {
                if (cancelled) return;
                const merged = (data?.data || []).reduce((acc, item) => {
                    const name = item?.country || t('Inconnu');
                    acc[name] = (acc[name] || 0) + (Number.parseInt(item?.users, 10) || 0);
                    return acc;
                }, {});
                setCountries(Object.entries(merged).sort((a, b) => b[1] - a[1]).slice(0, 8));
            })
            .catch(() => !cancelled && setCountries([]));
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const usersChart = useMemo(() => {
        if (!newUsers && !activeUsers) return null;
        const value = (source, key) => Math.round(((source || {})[key] || {}).value * multiplier) || 0;
        const peak = Math.max(0, ...PERIODS.map(([key]) => Math.max(value(newUsers, key), value(activeUsers, key))));
        const axisMax = Math.max(5, Math.ceil(peak * 1.15)); // jamais 0 : sinon l'échelle devient « Infinity »
        return {
            series: [
                { name: t('Nouveaux utilisateurs'), data: PERIODS.map(([key]) => value(newUsers, key)) },
                { name: t('Utilisateurs actifs'), data: PERIODS.map(([key]) => value(activeUsers, key)) },
            ],
            options: {
                ...COMMON,
                colors: COLORS,
                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                xaxis: { categories: PERIODS.map(([, label]) => t(label)) },
                yaxis: { min: 0, max: axisMax, tickAmount: Math.min(axisMax, 5), labels: { formatter: (v) => Math.round(v) } },
                tooltip: { shared: true, intersect: false },
            },
        };
    }, [newUsers, activeUsers, multiplier, t]);

    const earningsChart = useMemo(() => {
        const rows = (monetizedAccounts || []).slice(0, 8);
        if (rows.length === 0) return null;
        const amount = (row, kind) => Number(((row.balances || {})[kind] || {}).usd) || 0;
        return {
            series: [
                { name: t('Pièces'), data: rows.map((row) => amount(row, 'coins')) },
                { name: t('Likes'), data: rows.map((row) => amount(row, 'likes')) },
                { name: t('Vues'), data: rows.map((row) => amount(row, 'views')) },
            ],
            options: {
                ...COMMON,
                chart: { ...COMMON.chart, stacked: true },
                colors: COLORS,
                plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '60%' } },
                xaxis: { labels: { formatter: (v) => `$${Number(v).toFixed(2)}` } },
                yaxis: { labels: { maxWidth: 140 } },
                labels: rows.map((row) => (row.user || {}).user_username ? `@${row.user.user_username}` : '—'),
                tooltip: { y: { formatter: (v) => `${Number(v).toFixed(2)} USD` } },
            },
            categories: rows.map((row) => ((row.user || {}).user_username ? `@${row.user.user_username}` : '—')),
        };
    }, [monetizedAccounts, t]);

    const countriesChart = useMemo(() => {
        if (!countries || countries.length === 0) return null;
        return {
            series: [{ name: t('Utilisateurs actifs'), data: countries.map(([, users]) => users) }],
            options: {
                ...COMMON,
                colors: [COLORS[0]],
                legend: { show: false },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: false } },
                xaxis: { min: 0, categories: countries.map(([name]) => name), labels: { formatter: (v) => Math.round(v) } },
            },
        };
    }, [countries, t]);

    const emptyLabel = t('Aucune donnée à afficher.');

    return (
        <div className="row clearfix equal-height-row">
            <div className="col-lg-6 col-md-12">
                <ChartCard
                    title={t('Nouveaux utilisateurs et utilisateurs actifs')}
                    subtitle={t('Par période')}
                    loading={loadingUsers}
                    empty={!usersChart}
                    emptyLabel={emptyLabel}
                >
                    {usersChart && <ReactApexChart options={usersChart.options} series={usersChart.series} type="bar" height={300} />}
                </ChartCard>
            </div>
            <div className="col-lg-6 col-md-12">
                <ChartCard
                    title={t('Pays avec le plus d\'utilisateurs actifs')}
                    subtitle={t('Top 8')}
                    loading={countries === null}
                    empty={!countriesChart}
                    emptyLabel={emptyLabel}
                >
                    {countriesChart && <ReactApexChart options={countriesChart.options} series={countriesChart.series} type="bar" height={300} />}
                </ChartCard>
            </div>
            <div className="col-12">
                <ChartCard
                    title={t('Gains des comptes monétisés (USD)')}
                    subtitle={t('Pièces, likes et vues')}
                    loading={loadingMonetized}
                    empty={!earningsChart}
                    emptyLabel={emptyLabel}
                >
                    {earningsChart && (
                        <ReactApexChart
                            options={{ ...earningsChart.options, xaxis: { ...earningsChart.options.xaxis, categories: earningsChart.categories } }}
                            series={earningsChart.series}
                            type="bar"
                            height={Math.max(260, earningsChart.categories.length * 44 + 80)}
                        />
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

export default DashboardCharts;
